import React, { useState, useEffect } from 'react';
import './SchoolDetail.css';
import StudentsTable from './StudentsTable';
import BagsTable from './BagsTable';
import ActivityTable from './ActivityTable';
import OrdersTable from './OrdersTable';
import ReorderBreakdown from './ReorderBreakdown';

function SchoolDetail({
  students,
  bags,
  users,
  invites,
  events,
  orders,
  schoolId,
  schoolName,
  schoolDistrict,
  schoolNotes,
  schoolInternal,
  schoolArchived,
  onRefreshSchools,
  onBack,
  onRefreshStudents,
  onRefreshUsers,
  onRefreshInvites,
  onLoadEvents,
  onLoadOrders,
  onSchoolDeleted,
  // Which tab to land on. Set when the global search sends you here, so a serial opens Faraday
  // Bags and a person or student opens Students rather than always defaulting to the roster.
  initialTab
}) {
  // Read-only test badge in the header; the Mark-test toggle lives in the schools list.
  const [internalOn, setInternalOn] = useState(!!schoolInternal);
  useEffect(() => { setInternalOn(!!schoolInternal); }, [schoolInternal]);
  const [archivedOn, setArchivedOn] = useState(!!schoolArchived);
  useEffect(() => { setArchivedOn(!!schoolArchived); }, [schoolArchived]);
  const [archiving, setArchiving] = useState(false);
  const handleArchiveSchool = async () => {
    if (archiving) return;
    const next = !archivedOn;
    setArchiving(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/archived`, {
        method: 'PATCH',
        headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`), 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: next }),
      });
      if (response.ok) { setArchivedOn(next); if (onRefreshSchools) onRefreshSchools(); }
      else alert('Failed to update the school. Please try again.');
    } catch (err) {
      console.error('Error archiving school:', err);
      alert('Error updating the school. Please try again.');
    } finally {
      setArchiving(false);
    }
  };
  const [activeTab, setActiveTab] = useState(initialTab || 'students');
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);

  // Move-school-into-district control. Districts are matched by name to preselect the current one.
  const [districts, setDistricts] = useState([]);
  const [districtChoice, setDistrictChoice] = useState('');   // '' = none, else district id (string)
  const [initialChoice, setInitialChoice] = useState('');
  const [savingDistrict, setSavingDistrict] = useState(false);
  const [districtMsg, setDistrictMsg] = useState('');

  const adminAuth = () => 'Basic ' + btoa(`${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`);

  useEffect(() => {
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    fetch(`${apiEndpoint}/admin/districts`, { headers: { 'Authorization': adminAuth() }, cache: 'no-store' })
      .then(r => (r.ok ? r.json() : []))
      .then(list => {
        setDistricts(list);
        const match = list.find(d => (d.name || '').trim() === (schoolDistrict || '').trim());
        const id = match ? String(match.id) : '';
        setDistrictChoice(id);
        setInitialChoice(id);
        setDistrictMsg('');
      })
      .catch(() => {});
  }, [schoolId, schoolDistrict]);

  const handleSaveDistrict = async () => {
    if (savingDistrict) return;
    setSavingDistrict(true);
    setDistrictMsg('');
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const res = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/district`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': adminAuth() },
        body: JSON.stringify({ districtId: districtChoice ? Number(districtChoice) : null }),
      });
      if (res.ok) {
        setInitialChoice(districtChoice);
        setDistrictMsg('Saved.');
        if (onRefreshSchools) await onRefreshSchools();
      } else {
        const e = await res.json().catch(() => ({}));
        setDistrictMsg(e.error || 'Failed to save.');
      }
    } catch (err) {
      console.error('Error saving district:', err);
      setDistrictMsg('Error saving. Please try again.');
    } finally {
      setSavingDistrict(false);
    }
  };

  // Delete-school flow (type-to-confirm).
  const [showDeleteSchool, setShowDeleteSchool] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingSchool, setDeletingSchool] = useState(false);
  const [deleteSchoolError, setDeleteSchoolError] = useState(null);

  const closeDeleteSchool = () => { if (deletingSchool) return; setShowDeleteSchool(false); setDeleteConfirmText(''); setDeleteSchoolError(null); };

  const handleDeleteSchool = async () => {
    if (deletingSchool) return;
    setDeletingSchool(true);
    setDeleteSchoolError(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        setShowDeleteSchool(false);
        if (onSchoolDeleted) onSchoolDeleted();
      } else {
        setDeleteSchoolError('Failed to delete school. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting school:', err);
      setDeleteSchoolError('Error deleting school. Please try again.');
    } finally {
      setDeletingSchool(false);
    }
  };

  // Editable internal school-level notes (general, not tied to one order).
  const [notesDraft, setNotesDraft] = useState(schoolNotes || '');
  const [savedNotes, setSavedNotes] = useState(schoolNotes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  useEffect(() => { setNotesDraft(schoolNotes || ''); setSavedNotes(schoolNotes || ''); }, [schoolId, schoolNotes]);

  const handleSaveSchoolNotes = async () => {
    if (savingNotes) return;
    setSavingNotes(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
        body: JSON.stringify({ notes: notesDraft })
      });
      if (response.ok) {
        setSavedNotes(notesDraft);
      } else {
        alert('Failed to save school notes. Please try again.');
      }
    } catch (err) {
      console.error('Error saving school notes:', err);
      alert('Error saving school notes. Please try again.');
    } finally {
      setSavingNotes(false);
    }
  };

  // Load events when Activity tab is clicked
  useEffect(() => {
    if (activeTab === 'activity' && !eventsLoaded && onLoadEvents) {
      onLoadEvents();
      setEventsLoaded(true);
    }
  }, [activeTab, eventsLoaded, onLoadEvents]);

  // Load orders when the Orders tab is clicked
  useEffect(() => {
    if (activeTab === 'orders' && !ordersLoaded && onLoadOrders) {
      onLoadOrders();
      setOrdersLoaded(true);
    }
  }, [activeTab, ordersLoaded, onLoadOrders]);

  return (
    <div className="school-detail">
      <div className="school-detail-header">
        <div className="header-left">
          <button onClick={onBack} className="back-button">
            ← Back to Schools
          </button>
          <div className="school-info">
            <h2>{schoolName}{internalOn && <span className="test-badge">INTERNAL</span>}</h2>
            <div className="school-district-edit">
              <label>District:</label>
              <select
                value={districtChoice}
                onChange={(e) => { setDistrictChoice(e.target.value); setDistrictMsg(''); }}
                disabled={savingDistrict}
              >
                <option value="">Not in a district</option>
                {districts.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
              </select>
              {districtChoice !== initialChoice && (
                <button className="district-save-btn" onClick={handleSaveDistrict} disabled={savingDistrict}>
                  {savingDistrict ? 'Saving…' : 'Save'}
                </button>
              )}
              {districtMsg && <span className="district-msg">{districtMsg}</span>}
            </div>
          </div>
          <button className="archive-school-button" onClick={handleArchiveSchool} disabled={archiving} title={archivedOn ? 'Unarchive this school' : 'Archive this school (no longer needs support)'}>
            {archiving ? '…' : archivedOn ? 'Unarchive school' : 'Archive school'}
          </button>
          <button className="delete-school-button" onClick={() => { setShowDeleteSchool(true); setDeleteConfirmText(''); setDeleteSchoolError(null); }}>
            Delete school
          </button>
        </div>
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            Students
          </button>
          <button 
            className={`tab ${activeTab === 'bags' ? 'active' : ''}`}
            onClick={() => setActiveTab('bags')}
          >
            Faraday Bags
          </button>
          <button
            className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
          <button
            className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Orders
          </button>
          <button
            className={`tab ${activeTab === 'reorder' ? 'active' : ''}`}
            onClick={() => setActiveTab('reorder')}
          >
            Reorder
          </button>
        </div>
      </div>

      <div className="school-notes">
        <label className="school-notes-label">School notes (internal)</label>
        <textarea
          className="school-notes-input"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="General notes about this school: contacts, quirks, history…"
          rows={2}
        />
        <button
          className="school-notes-save"
          onClick={handleSaveSchoolNotes}
          disabled={savingNotes || notesDraft === savedNotes}
        >
          {savingNotes ? 'Saving…' : notesDraft === savedNotes ? 'Saved' : 'Save notes'}
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'students' ? (
          <StudentsTable 
            students={students}
            bags={bags}
            users={users}
            invites={invites}
            schoolId={schoolId}
            onRefreshStudents={onRefreshStudents}
            onRefreshUsers={onRefreshUsers}
            onRefreshInvites={onRefreshInvites}
          />
        ) : activeTab === 'bags' ? (
          <BagsTable 
            bags={bags}
            students={students}
            schoolId={schoolId}
            onRefreshBags={onRefreshStudents}
          />
        ) : activeTab === 'activity' ? (
          <ActivityTable
            events={events}
          />
        ) : activeTab === 'reorder' ? (
          <ReorderBreakdown schoolId={schoolId} />
        ) : (
          <OrdersTable
            orders={orders}
            onRefreshOrders={onLoadOrders}
          />
        )}
      </div>

      {showDeleteSchool && (
        <div className="modal-overlay" onClick={closeDeleteSchool}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete school</h3>
              <button className="modal-close" onClick={closeDeleteSchool}>×</button>
            </div>
            <div className="modal-body">
              <p>This permanently deletes <strong>{schoolName}</strong> and everything under it: its students, bags, orders, and user accounts.</p>
              <p className="warning-text">This cannot be undone. To confirm, type the school name below.</p>
              <div className="form-group">
                <label>Type <strong>{schoolName}</strong> to confirm</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={schoolName}
                  autoFocus
                />
              </div>
              {deleteSchoolError && <p className="warning-text">{deleteSchoolError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeDeleteSchool} disabled={deletingSchool}>
                Cancel
              </button>
              <button
                className="modal-button delete-confirm"
                onClick={handleDeleteSchool}
                disabled={deletingSchool || deleteConfirmText.trim() !== (schoolName || '').trim()}
              >
                {deletingSchool ? 'Deleting…' : 'Delete school'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolDetail;

